import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiChevronLeft, FiChevronRight, FiCalendar, FiClock,
    FiPlus, FiRefreshCw
} from "react-icons/fi";
import { MdAccessTime, MdPerson, MdKeyboardArrowDown, MdSearch, MdClose, MdContentCut, MdCalendarToday, MdSave } from "react-icons/md";

import {
  appointmentsAPI,
  usersAPI,
  servicesAPI,
  authAPI,
  workScheduleAPI,
} from "services/api";
// --- BİLEŞEN İMPORTLARI ---
import AppointmentDetailModal from "./components/AppointmentDetailModal";
import QuickCreateModal from "./components/QuickCreateModal";
import BreakModal from "./components/BreakModal";

// ============================================================
// YARDIMCI FONKSİYONLAR
// ============================================================

const formatISODate = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

const extractTime = (timeStr) => {
    if (!timeStr) return "00:00";
    const timeMatch = timeStr.match(/(\d{2}:\d{2})/);
    return timeMatch ? timeMatch[1] : "00:00";
};

const timeToFloat = (timeStr) => {
    if (!timeStr) return 0;
    const safeTime = extractTime(timeStr);
    const [hours, minutes] = safeTime.split(":").map(Number);
    return (hours || 0) + (minutes || 0) / 60;
};

const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (!timeStr) return "";
    const safeTime = extractTime(timeStr);
    const [hours, minutes] = safeTime.split(":").map(Number);
    const date = new Date();
    date.setHours(hours || 0, minutes || 0, 0, 0);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const getApptDuration = (appt) => {
  if (appt.appointment_duration) return parseInt(appt.appointment_duration);
  const sDur = parseInt(appt.service?.duration || 30);
  const o1Dur = appt.optionalService1
    ? parseInt(appt.optionalService1.duration || 0)
    : 0;
  const o2Dur = appt.optionalService2
    ? parseInt(appt.optionalService2.duration || 0)
    : 0;
  return sDur + o1Dur + o2Dur;
};

const doAppointmentsOverlap = (a1, a2) => {
  const start1 = timeToFloat(a1.appointment_time);
  const end1 = start1 + getApptDuration(a1) / 60;
  const start2 = timeToFloat(a2.appointment_time);
  const end2 = start2 + getApptDuration(a2) / 60;
  return Math.max(start1, start2) < Math.min(end1, end2);
};
const calculateOverlaps = (appointments) => {
    const overlapGroups = [];
    appointments.forEach(appt => {
        let placed = false;
        for (const group of overlapGroups) {
            if (group.some(existingAppt => doAppointmentsOverlap(appt, existingAppt))) {
                group.push(appt);
                placed = true;
                break;
            }
        }
        if (!placed) overlapGroups.push([appt]);
    });
    return overlapGroups;
};

// GRID AYARLARI
const START_HOUR = 8;
const END_HOUR = 23;
const HOUR_HEIGHT = 140; // Mobilde daha rahat tıklanabilmesi için yüksek tutuldu (15dk = 35px)

const timeSlots = [];
for (let h = START_HOUR; h < END_HOUR; h++) {
    for (let m = 0; m < 60; m += 15) {
        timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
}

// ============================================================
// ANA BİLEŞEN
// ============================================================
const AdminCalendarMobil = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        navigate("/admin/calendar", { replace: true });
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [navigate]);

  // --- TEMEL STATELER ---
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [servicesList, setServicesList] = useState([]);

  // --- MÜŞTERİ SEÇİM STATELERİ ---
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  const [customers, setCustomers] = useState([]); // <-- YENİ EKLENDİ

  // --- HİZMET SEÇİM STATELERİ ---
  const [serviceSearch, setServiceSearch] = useState("");
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [optServiceSearch, setOptServiceSearch] = useState("");
  const [showOptServiceDropdown, setShowOptServiceDropdown] = useState(false);

  // --- MODAL STATELERİ ---
  const [quickModalOpen, setQuickModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [molaModalOpen, setMolaModalOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [creating, setCreating] = useState(false);

  const [molaSlotsLoading, setMolaSlotsLoading] = useState(false);
  const [molaSlotsData, setMolaSlotsData] = useState([]);

  // --- YENİ EKLENEN STATELER ---
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [rawSlots, setRawSlots] = useState([]);

  const [quickForm, setQuickForm] = useState({
    date: formatISODate(currentDate),
    time: "",
    staffId: "",
    serviceId: "",
    optionalService1: "",
    customerId: "",
    name: "",
    surname: "",
    phone: "",
    birthDate: "",
    tag: "",
    note: "",
    isManual: false,
    customMinute: "00",
    customDuration: "",
  });

  const [molaForm, setMolaForm] = useState({
    staff_id: "",
    date: formatISODate(currentDate),
    selectedSlotIds: [],
  });
  // --- YENİ EKLENEN SAAT GETİRME (FETCH) İŞLEMİ ---
  useEffect(() => {
    const fetchSlots = async () => {
      const currentStaffId = quickForm.staffId || quickForm.staff_id;
      if (!quickModalOpen || !quickForm.date || !currentStaffId) {
        setAvailableSlots([]);
        return;
      }
      setSlotsLoading(true);
      try {
        const response = await workScheduleAPI.getAvailableSlots(
          currentStaffId,
          quickForm.date
        );
        const slotsForDate = response.available_slots?.[quickForm.date] || [];
        setRawSlots(slotsForDate);

        const now = new Date();
        const selectedDateObj = new Date(quickForm.date);
        const isToday = selectedDateObj.toDateString() === now.toDateString();
        const curH = now.getHours(),
          curM = now.getMinutes();

        const filtered = slotsForDate
          .filter((slot) => {
            const timeStr = slot.start_time || slot.time;
            if (!timeStr) return false;
            const isAvail =
              slot.is_available == true ||
              slot.is_available === 1 ||
              slot.is_available === "1";
            if (!isAvail) return false;
            if (isToday) {
              const [h, m] = timeStr.split(":").map(Number);
              if (h < curH || (h === curH && m <= curM)) return false;
            }
            return true;
          })
          .map((slot) => slot.start_time || slot.time)
          .sort();

        setAvailableSlots(filtered);
      } catch (err) {
        console.error("Saatler çekilemedi:", err);
        setAvailableSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [quickModalOpen, quickForm.date, quickForm.staffId, quickForm.staff_id]);

  // ============================================================
  // API İSTEKLERİ
  // ============================================================
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const formattedDate = formatISODate(currentDate);

      const [apptsRes, staffRes, servRes, custRes] = await Promise.all([
        appointmentsAPI.getAll({ date: formattedDate }),
        usersAPI.getStaff(),
        servicesAPI.getAll(),
        usersAPI.getCustomers(), // <-- MÜŞTERİLERİ DE ÇEKİYORUZ
      ]);
      setAppointments(apptsRes?.appointments || apptsRes?.data || []);

      const staffs = staffRes?.staff || staffRes?.data || [];
      setStaffList(staffs);

      if (staffs.length > 0 && !selectedStaffId) {
        setSelectedStaffId(staffs[0].id);
      }

      setServicesList(servRes?.services || servRes?.data || []);

      setCustomers(
        custRes?.customers ||
          custRes?.data ||
          (Array.isArray(custRes) ? custRes : [])
      );


    } catch (error) {
      console.error("Veri yükleme hatası:", error);
      toast.error("Takvim verileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!loading && scrollRef.current) {
      const now = new Date();
      if (now.toDateString() === currentDate.toDateString()) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        if (currentHour >= START_HOUR && currentHour < END_HOUR) {
          const topPos =
            (currentHour - START_HOUR + currentMinute / 60) * HOUR_HEIGHT;
          scrollRef.current.scrollTop = Math.max(0, topPos - 100);
        }
      }
    }
  }, [loading, currentDate]);

  // ============================================================
  // FİLTRELEME (KURŞUN GEÇİRMEZ TARİH VE PERSONEL FİLTRESİ)
  // ============================================================
  const currDateStr = formatISODate(currentDate);

  const displayedAppointments = useMemo(() => {
    return appointments.filter((appt) => {
      if (appt.status === "cancelled" || appt.status === "rejected")
        return false;

      // 1. TARİH KONTROLÜ (Backend tüm tarihleri gönderse bile UI sadece bugünü gösterir)
      const apptDate = appt.appointment_date?.split("T")[0];
      if (apptDate !== currDateStr) return false;

      // 2. PERSONEL KONTROLÜ
      const sId = appt.staff_id || appt.staff?.id;
      return String(sId) === String(selectedStaffId);
    });
  }, [appointments, selectedStaffId, currDateStr]);

  const overlapGroups = useMemo(
    () => calculateOverlaps(displayedAppointments),
    [displayedAppointments]
  );

  const getApptStyle = (appt) => {
    const group = overlapGroups.find((g) => g.includes(appt));
    let width = 100;
    let left = 0;
    let zIndex = 10;

    if (group && group.length > 1) {
      const index = group.indexOf(appt);
      width = 100 / group.length;
      left = width * index;
      zIndex = 10 + index;
    }

    const startFloat = timeToFloat(appt.appointment_time);
    const top = Math.max(0, (startFloat - START_HOUR) * HOUR_HEIGHT);
    const height = (getApptDuration(appt) / 60) * HOUR_HEIGHT;

    return {
      top: `${top}px`,
      height: `${height}px`,
      left: `${left}%`,
      width: `calc(${width}% - 2px)`,
      zIndex,
    };
  };

  const getStatusColors = (status) => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/50";
      case "approved":
        return "bg-brand-100 text-brand-800 border-brand-300 dark:bg-brand-900/40 dark:text-brand-300 dark:border-brand-700/50";
      case "completed":
        return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700/50";
      case "no_show":
        return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700/50";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600";
    }
  };

  // ============================================================
  // İŞLEMLER
  // ============================================================
  const prevDay = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };

  const nextDay = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };

  const handleSlotClick = (time) => {
    setQuickForm({
      date: formatISODate(currentDate),
      time: time,
      staffId: String(selectedStaffId || staffList[0]?.id || ""),
      serviceId: "",
      optionalService1: "",
      customerId: "",
      name: "",
      surname: "",
      phone: "",
      birthDate: "",
      tag: "",
      note: "",
      isManual: false,
      customMinute: "00",
      customDuration: "",
    });
    setIsNewCustomer(false);
    setCustomerSearch("");
    setFilteredCustomers([]);
    setQuickModalOpen(true);
  };

  const normalizeForSearch = (text) => {
    if (!text) return "";
    return text
      .replace(/İ|I|ı/g, "i")
      .replace(/Ğ|ğ/g, "g")
      .replace(/Ü|ü/g, "u")
      .replace(/Ş|ş/g, "s")
      .replace(/Ö|ö/g, "o")
      .replace(/Ç|ç/g, "c")
      .toLowerCase()
      .trim();
  };

  const handleCustomerSearch = (e) => {
    const term = e.target.value;
    setCustomerSearch(term);
    setQuickForm((prev) => ({ ...prev, customerName: term, customerId: "" }));

    // 2 harften azsa veya yeni müşteri ekleme modundaysa arama yapma
    if (term.length < 2 || isNewCustomer) {
      setFilteredCustomers([]);
      return;
    }

    // Aranan kelimeyi ve telefonu temizle
    const searchNorm = normalizeForSearch(term);
    const searchPhone = term.replace(/\D/g, ""); // Sadece rakamlar

    // Frontend'de kusursuz filtreleme
    const filtered = customers.filter((c) => {
      const nameNorm = normalizeForSearch(c.name);
      const surnameNorm = normalizeForSearch(c.surname);
      const fullNameNorm = normalizeForSearch(`${c.name} ${c.surname}`);
      const phoneClean = c.phone ? c.phone.replace(/\D/g, "") : "";

      // İsim veya Soyisim içinde geçiyor mu?
      const isNameMatch =
        nameNorm.includes(searchNorm) ||
        surnameNorm.includes(searchNorm) ||
        fullNameNorm.includes(searchNorm);

      // Telefon numarasının GÖVDESİNDE geçiyor mu? (örn: 7893 -> 905324247893 içinde var)
      const isPhoneMatch =
        searchPhone.length > 0 && phoneClean.includes(searchPhone);

      return isNameMatch || isPhoneMatch;
    });

    setFilteredCustomers(filtered);
  };

  const selectCustomer = (customer) => {
    setQuickForm((prev) => ({
      ...prev,
      customerId: customer.id,
      name: customer.name || "",
      surname: customer.surname || "",
      phone: customer.phone || "",
    }));
    setCustomerSearch(
      `${customer.name} ${customer.surname || ""} - ${customer.phone}`
    );
    setFilteredCustomers([]);
  };
  const handleQuickCreate = async (e) => {
    if (e) e.preventDefault();
    setCreating(true);

    try {
      let finalCustomerId = quickForm.customerId;

      // EĞER YENİ MÜŞTERİ SEÇİLMİŞSE VEYA MEVCUT MÜŞTERİ SEÇİLMEMİŞSE (Yeni Kayıt)
      if (isNewCustomer || !finalCustomerId) {
        // 1. BOŞ ALAN KONTROLÜ (Artık doğru değişkenlere bakıyoruz)
        if (!quickForm.name || !quickForm.phone) {
          toast.error("İsim ve Telefon alanları zorunludur.");
          setCreating(false);
          return;
        }

        // 2. TELEFON NUMARASI FORMATLAMA (Sadece rakamları ayıkla)
        const cleanPhone = quickForm.phone.replace(/\D/g, "");

        // Telefon 10 haneli mi ve 5 ile mi başlıyor kontrolü
        if (cleanPhone.length !== 10 || !cleanPhone.startsWith("5")) {
          toast.error(
            "Telefon numarası 5 ile başlamalı ve 10 haneli olmalıdır."
          );
          setCreating(false);
          return;
        }

        // Backend'e gidecek son hali
        const formattedPhone = "+90" + cleanPhone;

        // 3. YENİ MÜŞTERİ PAYLOAD'I
        const newCustPayload = {
          name: quickForm.name,
          surname: quickForm.surname || " ", // Soyisim yoksa boşluk atar
          phone: formattedPhone, // +90'lı tam numara
          email: `mail@giriniz.com`,
          password: "123456!",
          password_confirmation: "123456!",
          role: "customer",
          birth_date: quickForm.birthDate || "0001-01-01", // Boşsa varsayılan tarih
          tag: quickForm.tag || null, // Boşsa null gönderir
        };

        const res = await authAPI.register(newCustPayload);
        if (res.success || res.user) {
          finalCustomerId = res.user?.id || res.data?.id;
        } else {
          toast.error("Müşteri oluşturulamadı.");
          setCreating(false);
          return;
        }
      }

      // 4. RANDEVU OLUŞTURMA PAYLOAD'I
      const payload = {
        customer_id: finalCustomerId,
        staff_id: quickForm.staffId,
        service_id: quickForm.serviceId,
        optional_service_id_1: quickForm.optionalService1 || null,
        appointment_date: quickForm.date,
        appointment_time: quickForm.time,
        note: quickForm.note || "Mobil takvimden eklendi",
      };

      await appointmentsAPI.create(payload);
      toast.success("Randevu başarıyla oluşturuldu.");
      setQuickModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Kayıt işlemi başarısız.");
    } finally {
      setCreating(false);
    }
  };

  const handleCancelAppt = async () => {
    if (window.confirm("Bu randevuyu iptal etmek istediğinize emin misiniz?")) {
      try {
        await appointmentsAPI.updateStatus(selectedAppt.id, "cancelled");
        toast.success("Randevu iptal edildi.");
        setDetailsModalOpen(false);
        fetchData();
      } catch (err) {
        toast.error("İptal edilirken bir hata oluştu.");
      }
    }
  };

  const handleNoShowAppt = async () => {
    if (
      window.confirm("Müşteriyi gelmedi olarak işaretlemek istiyor musunuz?")
    ) {
      try {
        await appointmentsAPI.updateStatus(selectedAppt.id, "no_show");
        toast.success("Gelmedi olarak işaretlendi.");
        setDetailsModalOpen(false);
        fetchData();
      } catch (err) {
        toast.error("İşlem başarısız.");
      }
    }
  };

  const handleAddToBlacklist = async () => {
    toast.info("Kara liste özelliği hazırlanıyor.");
  };

  const handleToggleBreak = async () => {
    toast.info("Mola servisi bağlanıyor...");
  };

  const handleSaveMola = async () => {
    toast.info("Mola servisi bağlanıyor...");
  };

  // ============================================================
  // RENDER EKRANI
  // ============================================================
  if (loading && appointments.length === 0 && staffList.length === 0) {
    return (
      <div className="flex h-full w-full animate-pulse flex-col gap-4 bg-gray-50 p-4 dark:bg-navy-900">
        <div className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
        <div className="w-full flex-1 rounded-2xl bg-gray-200 dark:bg-navy-700" />
      </div>
    );
  }

  return (
    <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-white dark:bg-navy-900">
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar
        theme="colored"
      />

      {/* 1. ÜST BAR: TARİH VE PERSONEL SEÇİMİ */}
      <div className="z-20 flex-none border-b border-gray-100 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex items-center justify-between p-4 pb-2">
          <button
            onClick={prevDay}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-600 transition-all hover:bg-brand-500 hover:text-white active:scale-95 dark:border-white/5 dark:bg-navy-700 dark:text-gray-300"
          >
            <FiChevronLeft size={20} />
          </button>

          <div className="group relative flex cursor-pointer flex-col items-center">
            {/* GİZLİ NATIVE DATE PICKER (Mobilde telefonun kendi lüks takvimini açar) */}
            <input
              type="date"
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              value={formatISODate(currentDate)}
              onChange={(e) => {
                if (e.target.value) {
                  // Kullanıcı takvimden tarihi seçtiği an tek bir API isteği atarak o güne atlar
                  setCurrentDate(new Date(e.target.value));
                }
              }}
            />

            <span className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 transition-colors group-active:text-brand-500 dark:text-gray-500">
              {currentDate.toLocaleDateString("tr-TR", { weekday: "long" })}
            </span>
            <span className="flex items-center gap-2 text-lg font-black text-navy-700 transition-colors group-active:text-brand-500 dark:text-white">
              <FiCalendar className="text-brand-500" />
              {currentDate.toLocaleDateString("tr-TR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              {/* Kullanıcıya buranın tıklanabilir olduğunu hissettirecek ufak bir ok */}
              <MdKeyboardArrowDown className="-ml-1 text-gray-400" size={16} />
            </span>
          </div>

          <button
            onClick={nextDay}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-600 transition-all hover:bg-brand-500 hover:text-white active:scale-95 dark:border-white/5 dark:bg-navy-700 dark:text-gray-300"
          >
            <FiChevronRight size={20} />
          </button>
        </div>

        <div className="custom-sidebar-scroll flex gap-2 overflow-x-auto px-4 pb-4">
          {staffList.map((staff) => (
            <button
              key={staff.id}
              onClick={() => setSelectedStaffId(staff.id)}
              className={`flex-none whitespace-nowrap rounded-xl border px-5 py-2.5 text-sm font-bold transition-all ${
                String(selectedStaffId) === String(staff.id)
                  ? "border-brand-500 bg-brand-500 text-white shadow-md shadow-brand-500/30"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700"
              }`}
            >
              {staff.name} {staff.surname}
            </button>
          ))}
        </div>
      </div>

      {/* 2. KAYDIRILABİLİR TAKVİM IZGARASI */}
      <div
        className="relative flex-1 overflow-y-auto bg-gray-50 p-2 dark:bg-navy-900"
        ref={scrollRef}
      >
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
          </div>
        ) : (
          <div
            className="relative w-full overflow-hidden rounded-[20px] border border-gray-100 bg-white shadow-sm dark:border-white/5 dark:bg-navy-800"
            style={{ height: `${(END_HOUR - START_HOUR) * HOUR_HEIGHT}px` }}
          >
            {/* Saat Çizgileri */}
            {timeSlots.map((time, index) => {
              const isHour = time.endsWith(":00");
              return (
                <div
                  key={time}
                  className="absolute flex w-full border-t border-gray-100 dark:border-navy-700/50"
                  style={{
                    top: `${index * (HOUR_HEIGHT / 4)}px`,
                    height: `${HOUR_HEIGHT / 4}px`,
                  }}
                >
                  <div className="relative w-14 flex-none border-r border-gray-100 bg-gray-50 dark:border-navy-700/50 dark:bg-navy-900/50">
                    {isHour && (
                      <span className="absolute -top-2.5 right-2 text-[10px] font-bold text-gray-400 dark:text-gray-500">
                        {time}
                      </span>
                    )}
                  </div>
                  <div
                    className={`flex-1 transition-colors active:bg-brand-50 dark:active:bg-brand-900/20 ${
                      !isHour
                        ? "border-t border-dashed border-gray-100 dark:border-navy-700/30"
                        : ""
                    }`}
                    onClick={() => handleSlotClick(time)}
                  />
                </div>
              );
            })}

            {/* POINTER-EVENTS-NONE: Boşluklara tıklamayı engellememek için */}
            <div className="pointer-events-none absolute bottom-0 left-14 right-0 top-0 p-1">
              {displayedAppointments.map((appt) => {
                const style = getApptStyle(appt);
                const statusColor = getStatusColors(appt.status);

                return (
                  <div
                    key={appt.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAppt(appt);
                      setDetailsModalOpen(true);
                    }}
                    className={`pointer-events-auto absolute flex cursor-pointer flex-col justify-start overflow-hidden rounded-xl border p-2 shadow-sm transition-transform active:scale-95 ${statusColor}`}
                    style={style}
                  >
                    <div className="mb-0.5 flex items-center gap-1 text-[10px] font-black leading-none opacity-80">
                      <FiClock size={10} /> {extractTime(appt.appointment_time)}
                    </div>
                    <div className="truncate text-xs font-bold leading-tight drop-shadow-sm">
                      {appt.customer?.name} {appt.customer?.surname}
                    </div>
                    {appt.duration >= 30 && (
                      <div className="mt-0.5 truncate text-[10px] font-medium opacity-90">
                        {appt.service?.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Yüzen Butonlar (Yenileme ve Ekleme) */}
      <div className="absolute bottom-6 right-6 z-30 flex flex-col gap-3">
        <button
          onClick={fetchData}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-100 bg-white text-brand-500 shadow-lg transition-all hover:bg-gray-50 active:scale-95 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700"
        >
          <FiRefreshCw size={20} className={loading ? "animate-spin" : ""} />
        </button>
        <button
          onClick={() => handleSlotClick("09:00")}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-white shadow-xl shadow-brand-500/40 transition-all hover:bg-brand-600 active:scale-95"
        >
          <FiPlus size={28} />
        </button>
      </div>

      {/* MODALLAR */}
      {detailsModalOpen && selectedAppt && (
        <AppointmentDetailModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          appt={selectedAppt}
          onCancel={handleCancelAppt}
          onNoShow={handleNoShowAppt}
          onAddToBlacklist={handleAddToBlacklist}
        />
      )}

      {quickModalOpen && (
        <QuickCreateModal
          open={quickModalOpen}
          onClose={() => setQuickModalOpen(false)}
          quickForm={quickForm}
          setQuickForm={setQuickForm}
          servicesList={servicesList}
          staffList={staffList}
          availableSlots={availableSlots}
          slotsLoading={slotsLoading}
          isNewCustomer={isNewCustomer}
          setIsNewCustomer={setIsNewCustomer}
          customerSearch={customerSearch}
          setCustomerSearch={setCustomerSearch}
          filteredCustomers={filteredCustomers}
          handleCustomerSearch={handleCustomerSearch}
          selectCustomer={selectCustomer}
          serviceSearch={serviceSearch}
          setServiceSearch={setServiceSearch}
          showServiceDropdown={showServiceDropdown}
          setShowServiceDropdown={setShowServiceDropdown}
          optServiceSearch={optServiceSearch}
          setOptServiceSearch={setOptServiceSearch}
          showOptServiceDropdown={showOptServiceDropdown}
          setShowOptServiceDropdown={setShowOptServiceDropdown}
          creating={creating}
          onSubmit={handleQuickCreate}
          onToggleBreak={handleToggleBreak}
        />
      )}

      {molaModalOpen && (
        <BreakModal
          open={molaModalOpen}
          onClose={() => setMolaModalOpen(false)}
          staffList={staffList}
          molaForm={molaForm}
          setMolaForm={setMolaForm}
          molaSlotsData={molaSlotsData}
          molaSlotsLoading={molaSlotsLoading}
          creating={creating}
          onSave={handleSaveMola}
        />
      )}

      {/* Scrollbar CSS */}
      <style jsx>{`
        .custom-sidebar-scroll::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .custom-sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-sidebar-scroll::-webkit-scrollbar-thumb {
          background: rgba(163, 174, 208, 0.3);
          border-radius: 10px;
        }
        .dark .custom-sidebar-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

export default AdminCalendarMobil;